import React, { useState } from "react";
import { NavLink, Outlet, Navigate, Link } from "react-router-dom";
import {
  LayoutDashboard, Users, CreditCard, Database, ScrollText,
  Shield, LogOut, Menu, X, ArrowLeft
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

const NAV = [
  { to: "/admin/dashboard", icon: LayoutDashboard, label: "Visão geral" },
  { to: "/admin/users", icon: Users, label: "Usuários" },
  { to: "/admin/subscriptions", icon: CreditCard, label: "Assinaturas" },
  { to: "/admin/database", icon: Database, label: "Banco de dados" },
  { to: "/admin/logs", icon: ScrollText, label: "Logs" },
];

export default function AdminShell() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);

  if (!user) return <Navigate to="/login" replace />;
  if (!user.is_admin) return <Navigate to="/app/dashboard" replace />;

  const NavItems = ({ onNav }) => (
    <nav className="space-y-1">
      {NAV.map(({ to, icon: Icon, label }) => (
        <NavLink
          key={to}
          to={to}
          onClick={onNav}
          data-testid={`admin-nav-${label.toLowerCase().replace(/\s/g, "-")}`}
          className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all ${
              isActive
                ? "bg-primary/15 text-primary border border-primary/30"
                : "text-muted-foreground hover:text-foreground hover:bg-accent/50 border border-transparent"
            }`
          }
        >
          <Icon className="w-4 h-4 shrink-0" />
          <span className="truncate">{label}</span>
        </NavLink>
      ))}
    </nav>
  );

  return (
    <div className="min-h-screen flex bg-background">
      <aside className="hidden lg:flex w-64 shrink-0 flex-col border-r border-border bg-[#070F1B] sticky top-0 h-screen">
        <div className="p-5 border-b border-border">
          <Link to="/admin/dashboard" className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-md bg-red-500/90 flex items-center justify-center">
              <Shield className="w-4 h-4 text-white" />
            </div>
            <div>
              <span className="font-display font-bold tracking-tight block leading-tight">SmartStockAI</span>
              <span className="text-[10px] text-red-400 font-mono uppercase tracking-widest">ADMIN</span>
            </div>
          </Link>
        </div>
        <div className="flex-1 overflow-y-auto p-3">
          <NavItems />
        </div>
        <div className="p-3 border-t border-border space-y-1">
          <Link to="/app/dashboard">
            <Button variant="ghost" size="sm" className="w-full justify-start text-muted-foreground hover:text-foreground">
              <ArrowLeft className="w-4 h-4 mr-2" /> Ir para app
            </Button>
          </Link>
          <div className="px-2 py-2">
            <div className="text-xs text-muted-foreground">Admin</div>
            <div className="text-sm font-medium truncate">{user?.name}</div>
            <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
          </div>
          <Button data-testid="admin-logout-btn" onClick={logout} variant="ghost" size="sm" className="w-full justify-start text-muted-foreground hover:text-foreground">
            <LogOut className="w-4 h-4 mr-2" /> Sair
          </Button>
        </div>
      </aside>

      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/60" onClick={() => setOpen(false)} />
          <aside className="relative w-72 bg-[#070F1B] border-r border-border flex flex-col">
            <div className="p-5 border-b border-border flex items-center justify-between">
              <span className="font-display font-bold">SmartStockAI Admin</span>
              <button onClick={() => setOpen(false)}><X className="w-5 h-5" /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-3"><NavItems onNav={() => setOpen(false)} /></div>
          </aside>
        </div>
      )}

      <div className="flex-1 min-w-0 flex flex-col">
        <header className="lg:hidden border-b border-border bg-[#050B14]/70 backdrop-blur-xl px-4 py-3 flex items-center justify-between sticky top-0 z-30">
          <button onClick={() => setOpen(true)}><Menu className="w-5 h-5" /></button>
          <span className="font-display font-bold text-sm">Admin</span>
          <div className="w-5" />
        </header>
        <main className="flex-1 p-6 lg:p-10 max-w-[1600px] w-full mx-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
